import { useState } from 'react';
import { api } from '@/api/client';
import type { Account, AppConfig } from '@/api/types';
import { Alert, Chip } from '@/components/ui';
import { offersWhatsApp } from '@/features/auth/channels';
import { useSession } from '@/hooks/session';
import { useT } from '@/i18n';

/**
 * "Where should we send your report and receipts?" (spec WA10). An explicit pick — Meta's
 * opt-in rule and DPDPA both need one, so nothing is preselected until the parent has chosen.
 * Asked only when WhatsApp is live and the number is verified; otherwise reports go by email.
 */

type Pick = 'whatsapp' | 'email' | 'both' | 'app';

const PREFS: Record<Pick, { email: boolean; whatsapp: boolean }> = {
  whatsapp: { email: false, whatsapp: true },
  email: { email: true, whatsapp: false },
  both: { email: true, whatsapp: true },
  app: { email: false, whatsapp: false },
};

const pickOf = (prefs: Account['deliveryPreferences']): Pick | undefined => {
  if (!prefs) return undefined;
  if (prefs.whatsapp) return prefs.email ? 'both' : 'whatsapp';
  return prefs.email ? 'email' : 'app';
};

export const deliveryQuestionShown = (account: Account | null | undefined, config: AppConfig | null | undefined): boolean =>
  Boolean(account?.phoneVerified) && offersWhatsApp(config);

export const DeliveryChoice = ({ source }: { source: 'checkout' | 'settings' }) => {
  const t = useT();
  const { account, refresh } = useSession();
  const [saving, setSaving] = useState<Pick | null>(null);
  const [failed, setFailed] = useState(false);
  const chosen = saving ?? pickOf(account?.deliveryPreferences);
  const options: Pick[] = account?.emailVerified ? ['whatsapp', 'email', 'both'] : ['whatsapp', 'app'];

  const choose = async (pick: Pick) => {
    setSaving(pick);
    setFailed(false);
    try {
      await api.me.setDeliveryPreferences({ ...PREFS[pick], source });
      await refresh();
    } catch {
      setFailed(true);
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="space-y-3">
      <h2 className="text-label text-ink">{source === 'checkout' ? t('delivery.title') : t('delivery.settingsTitle')}</h2>
      <div role="radiogroup" aria-label={t('delivery.title')} className="flex flex-wrap gap-2">
        {options.map((pick) => (
          <Chip key={pick} role="radio" selected={chosen === pick} onToggle={() => void choose(pick)}>
            {t(`delivery.${pick}`)}
          </Chip>
        ))}
      </div>
      <p className="text-caption text-ink-muted">{t('delivery.consent')}</p>
      {failed && <Alert tone="warn">{t('delivery.failed')}</Alert>}
    </div>
  );
};
